import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Box, Card, CardContent, Typography, Button, LinearProgress, Chip, Stack, 
  Select, MenuItem, FormControl, InputLabel, Alert, Drawer, IconButton,
  Divider, List, ListItem, ListItemText, ListItemIcon, Fade, Paper,
  Grid, Avatar, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { 
  PlayCircle as PlayIcon, 
  Stop as StopIcon, 
  CheckCircle as CheckIcon, 
  Cancel as CancelIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';

import useStudioStore from '../../state/studioStore';
import CrossToolSuggestions from '../../components/CrossToolSuggestions';
import { API, API_BASE_URL } from '../../lib/api';

export default function LiveTrialCore() {
  const { 
    setCurrentRun, 
    setCurrentSuite, 
    setCurrentVersion, 
    pushEvent,
    setWorkflowResults,
    setAgentInsights,
    currentSuite: storeCurrentSuite,
    currentVersion: storeCurrentVersion 
  } = useStudioStore();

  
  // Sidepanel state for seamless experience
  // Local state
  const [versionId, setVersionId] = useState('');
  const [suiteId, setSuiteId] = useState('');
  const [versions, setVersions] = useState([]);
  const [suites, setSuites] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [runId, setRunId] = useState('');
  const [completedCases, setCompletedCases] = useState([]);
  const [finalMetrics, setFinalMetrics] = useState(null);
  const [runSummary, setRunSummary] = useState(null); // derived summary after completion
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const eventSourceRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const lastEventAtRef = useRef(null);
  const runInProgress = useRef(false);
  const runsLoading = useRef(false);
  const sliceDataLoading = useRef(false);
  const diffDataLoading = useRef(false);
  const HEARTBEAT_TIMEOUT_MS = 30000;

  const closeEventStream = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    lastEventAtRef.current = null;
  }, []);
  
  // Sidepanel state for seamless experience
  const [resultsOpen, setResultsOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runs, setRuns] = useState([]);
  const [sliceData, setSliceData] = useState([]);
  const [diffData, setDiffData] = useState(null);
  const [agentJobs, setAgentJobs] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  
  const loadInitialData = useCallback(async (currentVersionId = '', currentSuiteId = '') => {
    setLoading(true);
    setError('');

    try {
      const [allVersions, suitesData] = await Promise.all([
        API.prompts.allVersions(),
        API.proof.suites()
      ]);

      setVersions(allVersions);
      setSuites(suitesData);

      if (allVersions.length > 0) {
        const existingVersion = allVersions.find(v => v.id === currentVersionId);
        setVersionId(existingVersion ? existingVersion.id : allVersions[0].id);
      } else {
        setVersionId('');
      }

      if (suitesData.length > 0) {
        const existingSuite = suitesData.find(s => s.id === currentSuiteId);
        setSuiteId(existingSuite ? existingSuite.id : suitesData[0].id);
      } else {
        setSuiteId('');
      }
    } catch (err) {
      console.error('LiveTrial data load failed:', err);
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

const loadRuns = async () => {
    if (runsLoading.current) return;
    runsLoading.current = true;
    try {
      const data = await API.proof.runs();
      setRuns(data);
    } catch (err) {
      console.error('Failed to load runs:', err);
    } finally {
      runsLoading.current = false;
    }
  };

  const loadSliceData = async (runId) => {
    if (sliceDataLoading.current) return;
    sliceDataLoading.current = true;
    try {
      const payload = await API.proof.slices(runId);
      if (payload && Array.isArray(payload.slices)) {
        setSliceData(payload.slices);
      } else if (Array.isArray(payload)) {
        setSliceData(payload);
      } else {
        setSliceData([]);
      }
    } catch (err) {
      console.error('Failed to load slice data:', err);
    } finally {
      sliceDataLoading.current = false;
    }
};

const loadDiffData = async (baselineId, candidateId) => {
  if (diffDataLoading.current) return;
    diffDataLoading.current = true;
    try {
      const data = await API.prompts.diff(baselineId, candidateId);
      setDiffData(data);
    } catch (err) {
      console.error('Failed to load diff data:', err);
    } finally {
      diffDataLoading.current = false;
    }
  };

  const loadAgentInsights = useCallback(async (runId, workflowData = null) => {
    if (!runId) {
      setAgentJobs([]);
      setRecommendations([]);
      setAgentInsights({ jobs: [], recommendations: [] }, 'agents');
      return;
    }
    try {
      const [jobs, recs] = await Promise.all([
        API.agents.jobsForRun(runId).catch(() => []),
        API.agents.recommendations().catch(() => []),
      ]);
      const jobList = Array.isArray(jobs) ? jobs : [];
      const recList = Array.isArray(recs) ? recs : [];
      setAgentJobs(jobList);
      setRecommendations(recList);
      setAgentInsights({ jobs: jobList, recommendations: recList }, 'agents');
    } catch (err) {
      console.error('Failed to load agent insights:', err);
    }
  }, [setAgentInsights]);

  useEffect(() => {
    if (selectedRunId) {
      loadAgentInsights(selectedRunId);
    }
  }, [selectedRunId, loadAgentInsights]);

  const startRun = async () => {
    if (runInProgress.current) return; // Prevent multiple runs
    if (!versionId || !suiteId) {
      setError('Please select version and suite');
      return;
    }

    runInProgress.current = true;
    setIsRunning(true);
    setProgress(0);
    setCompletedCases([]);
    setFinalMetrics(null);
    setError('');

    try {
      const eventSource = new EventSource(
        API.proof.streamRun(versionId, suiteId)
      );
      eventSourceRef.current = eventSource;
      lastEventAtRef.current = Date.now();
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
      }
      heartbeatTimerRef.current = window.setInterval(() => {
        if (!lastEventAtRef.current) {
          return;
        }
        if (Date.now() - lastEventAtRef.current > HEARTBEAT_TIMEOUT_MS) {
          closeEventStream();
          setIsRunning(false);
          runInProgress.current = false;
          setError('Live Trial connection timed out. Please retry the run.');
          pushEvent({
            type: 'error',
            title: 'Live Trial connection timed out',
            meta: { runId: runId || 'pending' },
            source: 'liveTrial'
          });
        }
      }, Math.max(HEARTBEAT_TIMEOUT_MS / 2, 5000));

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          lastEventAtRef.current = Date.now();
          
          switch (data.type) {
            case 'start':
              setRunId(data.run_id);
              break;
              
            case 'progress':
              setProgress(data.percent);
              break;
              
            case 'case':
              setCompletedCases(prev => [...prev, {
                ...data.result,
                id: Date.now() + Math.random() // Temp ID for animation
              }]);
              break;
              
            case 'complete': {
              // Ensure progress reaches 100
              setProgress(100);
              const metrics = data.metrics || {};
              setFinalMetrics(metrics);
              setIsRunning(false);
              const finalRunId = data.run_id || runId;
              setSelectedRunId(finalRunId);
              setResultsOpen(true); // Open results panel
              closeEventStream();

              // Derive summary from accumulated cases
              const total = completedCases.length;
              const passCount = completedCases.filter(c => c.pass).length;
              const failCount = total - passCount;
              const fastest = completedCases.reduce((m,c)=> c.latency_ms < m.latency_ms ? c : m, completedCases[0] || {latency_ms:0});
              const slowest = completedCases.reduce((m,c)=> c.latency_ms > m.latency_ms ? c : m, completedCases[0] || {latency_ms:0});
              const avgLatency = total ? Math.round(completedCases.reduce((s,c)=> s + (c.latency_ms||0),0)/total) : 0;
              setRunSummary({
                total,
                passCount,
                failCount,
                passRate: metrics?.pass_rate || (total ? passCount / total : 0),
                avgLatency,
                fastest: fastest?.latency_ms,
                slowest: slowest?.latency_ms
              });

              // Update global context and feed
              const completedRun = {
                id: finalRunId,
                version_id: versionId,
                suite_id: suiteId,
                metrics,
                created_at: Math.floor(Date.now() / 1000)
              };
              setCurrentRun(completedRun);
              const currentSuite = suites.find(s => s.id === suiteId);
              const currentVersion = versions.find(v => v.id === versionId);
              if (currentSuite) setCurrentSuite(currentSuite);
              if (currentVersion) setCurrentVersion(currentVersion);
              const workflowPayload = {
                run: completedRun,
                metrics,
                cases: completedCases,
                version: currentVersion,
                suite: currentSuite
              };
              setWorkflowResults(workflowPayload, 'liveTrial');
              loadAgentInsights(finalRunId, workflowPayload);
              const passRateValue = typeof metrics.pass_rate === 'number' ? metrics.pass_rate : null;
              const passRateLabel = passRateValue === null ? 'Unknown' : `${(passRateValue * 100).toFixed(0)}%`;
              pushEvent({
                type: 'run',
                title: `Live Trial completed: ${passRateLabel} pass rate`,
                meta: { metrics, runId: finalRunId }
              });
              // Preload slices for quicker analysis view
              loadSliceData(finalRunId);
              loadRuns();
              runInProgress.current = false;
              break; }
              
            default:
              if (data.error) {
                setError(data.error);
                setIsRunning(false);
                closeEventStream();
                runInProgress.current = false;
              }
          }
        } catch (err) {
          console.warn('Failed to parse SSE data:', err);
        }
      };

      eventSource.onerror = () => {
        setError('Connection lost');
        setIsRunning(false);
        closeEventStream();
        runInProgress.current = false;
      };

    } catch (err) {
      setError('Failed to start run: ' + err.message);
      setIsRunning(false);
      runInProgress.current = false;
    }
  };

  const stopRun = () => {
    closeEventStream();
    setIsRunning(false);
    runInProgress.current = false;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
        Watch your prompt versions get tested in real-time with animated progress
      </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }} disabled={loading || versions.length === 0}>
            <InputLabel>Prompt Version</InputLabel>
            <Select value={versionId} onChange={e => setVersionId(e.target.value)} label="Prompt Version">
              {loading && (
                <MenuItem disabled>Loading versions...</MenuItem>
              )}
              {!loading && versions.length === 0 && (
                <MenuItem disabled>No versions found</MenuItem>
              )}
              {versions.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }} disabled={loading || suites.length === 0}>
            <InputLabel>Test Suite</InputLabel>
            <Select value={suiteId} onChange={e => setSuiteId(e.target.value)} label="Test Suite">
              {loading && (
                <MenuItem disabled>Loading suites...</MenuItem>
              )}
              {!loading && suites.length === 0 && (
                <MenuItem disabled>No suites found</MenuItem>
              )}
              {suites.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name} ({s.cases?.length || 0} cases)</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button 
            variant="contained" 
            onClick={startRun} 
            disabled={isRunning || !versionId || !suiteId || versions.length === 0 || suites.length === 0}
            size="large"
            startIcon={<PlayIcon />}
          >
            {isRunning ? 'Running...' : 'Start Trial'}
          </Button>

          <Button
            variant="outlined"
            onClick={async () => {
              setVersions([]);
              setSuites([]);
              setVersionId('');
              setSuiteId('');
              setError('');
              await loadInitialData();
            }}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>

          {isRunning && (
            <Button variant="outlined" onClick={stopRun} startIcon={<StopIcon />}>
              Stop
            </Button>
          )}
        </Stack>

        {(versions.length === 0 || suites.length === 0) && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              DATA LOADING ISSUE:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              API Base: <code>{API_BASE_URL}</code>
            </Typography>
            {versions.length === 0 && suites.length === 0 && (
              <Typography variant="body2">
                ❌ No prompts OR suites loaded - database completely empty or API unreachable
              </Typography>
            )}
            {versions.length === 0 && suites.length > 0 && (
              <Typography variant="body2">
                ❌ Loaded {suites.length} suites but 0 prompt versions - prompts exist but have no versions
              </Typography>
            )}
            {suites.length === 0 && versions.length > 0 && (
              <Typography variant="body2">
                ❌ Loaded {versions.length} prompt versions but 0 test suites - no evaluation suites in database
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              Check browser console for detailed logs. Try the Refresh button to reload data.
            </Typography>
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isRunning && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              Progress: {Math.round(progress)}% • Run ID: {runId}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 8, 
                borderRadius: 1,
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #4CAF50 0%, #2196F3 100%)',
                }
              }} 
            />
          </Box>
        )}

        {finalMetrics && (
          <Card id="live-trial-summary" sx={{ p: 2, mb: 3, background: 'rgba(76, 175, 80, 0.1)', position: 'relative' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <AssessmentIcon sx={{ color: 'success.main' }} />
              <Typography variant="h6">✨ Live Trial Complete!</Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                icon={<CheckIcon />}
                label={`Pass Rate: ${(finalMetrics.pass_rate * 100).toFixed(1)}%`}
                color="success"
                variant="outlined"
              />
              <Chip 
                icon={<TimelineIcon />}
                label={`Coverage: ${(finalMetrics.citation_coverage * 100).toFixed(1)}%`}
                color="info" 
                variant="outlined"
              />
              <Chip 
                icon={<SpeedIcon />}
                label={`Avg Latency: ${finalMetrics.avg_latency_ms}ms`}
                color="warning"
                variant="outlined"
              />
              <Chip 
                label={`${finalMetrics.total_cases} cases`}
                variant="outlined"
              />
            </Stack>
            
            {runSummary && (
              <Box sx={{ p: 2, background: 'rgba(33, 150, 243, 0.08)', borderRadius: 1, border: '1px solid', borderColor: 'primary.light' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Run Summary</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>Total Cases: {runSummary.total}</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>Pass / Fail: {runSummary.passCount} / {runSummary.failCount}</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>Fastest: {runSummary.fastest}ms • Slowest: {runSummary.slowest}ms • Avg: {runSummary.avgLatency}ms</Typography>
              </Box>
            )}

            {/* Inline Cross-Tool Suggestions (formerly only in context feed) */}
            <Box sx={{ mt: 2 }}>
              <CrossToolSuggestions />
            </Box>
          </Card>
        )}

      {/* Animated Case Results */}
      {completedCases.length > 0 && (
        <Card sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <TimelineIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Case Results</Typography>
          </Stack>
          <Stack spacing={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
            {completedCases.map((case_, index) => (
              <Box
                key={case_.id}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: case_.pass ? 'success.main' : 'error.main',
                  borderRadius: 2,
                  background: case_.pass ? 'rgba(76, 175, 80, 0.05)' : 'rgba(244, 67, 54, 0.05)',
                  animation: 'slideInRight 0.3s ease-out',
                  '@keyframes slideInRight': {
                    from: { transform: 'translateX(100%)', opacity: 0 },
                    to: { transform: 'translateX(0)', opacity: 1 }
                  }
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    Case #{case_.idx}: {case_.notes}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip 
                      icon={case_.pass ? <CheckIcon /> : <CancelIcon />}
                      label={case_.pass ? 'PASS' : 'FAIL'} 
                      size="small"
                      color={case_.pass ? 'success' : 'error'}
                    />
                    <Chip
                      icon={<SpeedIcon />}
                      label={`${case_.latency_ms}ms`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
                {case_.citations && case_.citations.length > 0 && (
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Citations: {case_.citations.join(', ')}
                  </Typography>
                )}
                {case_.context_snippet && (
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.6 }}>
                    Context: {case_.context_snippet}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Card>
      )}

      {/* Results Sidepanel */}
      <Drawer
        anchor="right"
        open={resultsOpen}
        onClose={() => setResultsOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 480,
            bgcolor: 'background.default',
            borderLeft: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              Live Trial Results
            </Typography>
            <IconButton onClick={() => setResultsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          {finalMetrics && (
            <Fade in={true}>
              <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon />
                    Trial Complete!
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Chip 
                        icon={<CheckIcon />}
                        label={`${(finalMetrics.pass_rate * 100).toFixed(1)}% Pass`}
                        color="success"
                        variant="outlined"
                        sx={{ mb: 1, width: '100%' }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Chip 
                        icon={<SpeedIcon />}
                        label={`${finalMetrics.avg_latency_ms}ms Avg`}
                        color="info"
                        variant="outlined"
                        sx={{ mb: 1, width: '100%' }}
                      />
                    </Grid>
                  </Grid>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AnalyticsIcon />}
                    onClick={() => {
                      setAnalysisOpen(true);
                      if (selectedRunId) loadSliceData(selectedRunId);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Dive Deeper with Analysis
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          )}

          {agentJobs.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Agent Pipeline</Typography>
                <Stack spacing={1.5}>
                  {agentJobs.map((job) => (
                    <Stack key={job.id} direction="row" spacing={1.5} alignItems="center">
                      <Chip label={job.agent_type.toUpperCase()} size="small" />
                      <Chip
                        label={job.status}
                        color={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'error' : 'warning'}
                        size="small"
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {job.result?.summary || job.result?.reason || job.result?.message || (job.result?.changes ? `${job.result.changes.length} change(s)` : '')}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {recommendations.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Recommendations</Typography>
                <Stack spacing={1.2}>
                  {recommendations.map((rec) => (
                    <Box key={rec.run_id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Run {rec.run_id.slice(0, 8)} • {rec.created_at ? new Date(rec.created_at).toLocaleString() : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {rec.summary}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {completedCases.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Case Details ({completedCases.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {completedCases.map((case_, index) => (
                    <ListItem key={index} divider alignItems="flex-start">
                      <ListItemIcon>
                        {case_.pass ? <CheckIcon color="success" /> : <CancelIcon color="error" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={`Case #${case_.idx}`}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {case_.latency_ms}ms • AI Score: {typeof case_.ai_score === 'number' ? case_.ai_score.toFixed(2) : '—'}
                            </Typography>
                            {case_.notes && (
                              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.8 }}>
                                {case_.notes}
                              </Typography>
                            )}
                          {case_.citations && case_.citations.length > 0 && (
                            <Typography variant="caption" color="primary">
                              Citations: {case_.citations.join(', ')}
                            </Typography>
                          )}
                          {case_.context_snippet && (
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>
                              Context: {case_.context_snippet}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </Drawer>

      {/* Analysis Sidepanel */}
      <Drawer
        anchor="right"
        open={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 600,
            bgcolor: 'background.default',
            borderLeft: '1px solid',
            borderColor: 'divider',
            zIndex: 1300 // Higher than results panel
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsightsIcon color="secondary" />
              Deep Analysis
            </Typography>
            <IconButton onClick={() => setAnalysisOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack spacing={2}>
            <Button
              variant="outlined"
              startIcon={<TimelineIcon />}
              onClick={() => loadSliceData(selectedRunId)}
              fullWidth
            >
              Slice Analysis
            </Button>
            
            <Button
              variant="outlined" 
              startIcon={<TrendingUpIcon />}
              onClick={() => {
                const currentVersion = versions.find(v => v.id === versionId);
                if (currentVersion && runs.length > 0) {
                  const prevRun = runs.find(r => r.id !== selectedRunId);
                  if (prevRun) loadDiffData(prevRun.version_id, versionId);
                }
              }}
              fullWidth
            >
              Compare with Previous Run
            </Button>
          </Stack>

          {sliceData.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance Slices
                </Typography>
                {sliceData.map((slice, i) => {
                  const deltaPct = (slice.delta || 0) * 100;
                  return (
                    <Box key={i} sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                      <Typography variant="subtitle2">{slice.name}</Typography>
                      {slice.description && (
                        <Typography variant="body2" color="text.secondary">
                          {slice.description}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip label={`Pass: ${(slice.pass_rate * 100).toFixed(1)}%`} size="small" />
                        <Chip label={`Δ ${deltaPct >=0? '+':''}${deltaPct.toFixed(1)}%`} color={deltaPct>0? 'success': deltaPct<0? 'error':'default'} size="small" />
                        <Chip label={`${slice.count} cases`} variant="outlined" size="small" />
                      </Stack>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
